import { Injectable } from '@angular/core';
import { ApiResponse, ApiService } from '../api.service';
import { catchError, map, Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AddPrerequisiteAppVersionsData,
  AddPrerequisiteAppVersionsPayload,
  AddPrerequisiteAppVersionsResponse,
  Application,
  ApplicationData,
  AppVersion,
  AppVersionDetails,
  AppVersionDetailsRaw,
  AppVersionPrerequisite,
  AppVersionPrerequisiteRaw,
  CreateApplicationPayload,
  CreateVersionPayload,
  UpdateApplicationPayload,
  UpdateVersionPayload,
  VersionData,
} from '../../interfaces/application';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  constructor(private apiService: ApiService) {}

  createApp(payload: CreateApplicationPayload): Observable<any> {
    return this.apiService.post(`/v1/applications`, payload);
  }

  deleteApp(versionId: string): Observable<any> {
    return this.apiService.delete(`/v1/applications/versions/${versionId}`);
  }

  getApp(): Observable<any> {
    return this.apiService.get(`/v1/applications`);
  }

  getAllApplications(): Observable<ApplicationData[]> {
    return this.apiService.get<ApiResponse<Application[]>>(`/v1/applications`).pipe(
      map(({ data }) => {
        return data.map((app) => {
          return {
            appId: String(app.AppID),
            name: app.Name,
            description: app.Description,
            isPlugin: app.IsPlugin,
            versions: app.Versions.map((version) => {
              return {
                appVersionId: String(version.AppVersionID),
                version: version.Version,
                containerName: version.ContainerName,
                inputs: version.Inputs,
                releaseDate: version.ReleaseDate,
                isArchived: version.IsArchived,
                archivedAt: version.ArchivedAt,
                orgs: version.Orgs.map((org) => {
                  return {
                    orgId: String(org.OrgID),
                    name: org.Name,
                  };
                }),
              };
            }) as VersionData[],
          };
        });
      })
    );
  }

  getAppVersionDetails(versionId: string): Observable<AppVersionDetails> {
    return this.apiService.get<ApiResponse<AppVersionDetailsRaw>>(`/v1/applications/versions/${versionId}`).pipe(
      map(({ data }) => {
        return {
          appVersionId: String(data.AppVersionID),
          appId: String(data.AppID),
          version: data.Version,
          containerName: data.ContainerName,
          inputs: data.Inputs,
          multiRun: data.MultiRun,
          cpuCores: data.CPUCores,
          ramGb: data.RAM_GB,
          releaseDate: data.ReleaseDate,
          organizations: data.Organizations.map((org) => ({ orgId: String(org.OrgID), name: org.Name })),
          prerequisiteApps: (data.PrerequisiteApps ?? []).map((app) => ({
            appVersionId: String(app.AppVersionID),
            application: app.Application,
          })),
        };
      })
    );
  }

  createAppVersion(appId: number, payload: CreateVersionPayload): Observable<any> {
    const url = `/v1/applications/${appId}/versions`;
    return this.apiService.post(url, payload);
  }

  updateApplication(appId: number, payload: UpdateApplicationPayload): Observable<any> {
    return this.apiService.patch(`/v1/applications/${appId}`, payload);
  }

  updateVersion(versionId: number, payload: UpdateVersionPayload): Observable<any> {
    return this.apiService.patch(`/v1/applications/versions/${versionId}`, payload);
  }

  addPrereqAppVersion(
    appVersionId: string,
    { appVersionIdsToAdd }: { appVersionIdsToAdd: string[] }
  ): Observable<AddPrerequisiteAppVersionsData> {
    const url = `/v1/applications/versions/${appVersionId}/prerequisite-apps`;
    return this.apiService
      .post<ApiResponse<AddPrerequisiteAppVersionsResponse>>(url, { prerequisite_apps: appVersionIdsToAdd })
      .pipe(
        map(({ data }) => {
          return {
            added: data.Added,
            prerequisiteApps: data.PrerequisiteApps.map(({ AppVersionID, Application }) => {
              return {
                appVersionId: String(AppVersionID),
                application: Application,
              };
            }),
          };
        })
      );
  }

  // removePrereqAppVersion(
  //   appVersionId: string,
  //   { appVersionIdsToRemove }: { appVersionIdsToRemove: string[] }
  // ): Observable<AddPrerequisiteAppVersionsData> {
  //   const url = `/v1/applications/versions/${appVersionId}/prerequisite-apps`;
  //   return this.apiService
  //     .post<ApiResponse<AddPrerequisiteAppVersionsResponse>>(url, { prerequisite_apps: appVersionIdsToRemove })
  //     .pipe(
  //       map(({ data }) => {
  //         return {
  //           removed: data.Removed,
  //           prerequisiteApps: data.PrerequisiteApps.map(({ AppVersionID, Application }) => {
  //             return {
  //               appVersionId: String(AppVersionID),
  //               application: Application,
  //             };
  //           }),
  //         };
  //       })
  //     );
  // }

  assignAppToOrg(orgId: number, versionId: number): Observable<any> {
    return this.apiService.put(`/v1/organizations/${orgId}/app-versions/${versionId}`, {});
  }

  removeAppFromOrg(orgId: number, versionId: number): Observable<any> {
    return this.apiService.delete(`/v1/organizations/${orgId}/app-versions/${versionId}`);
  }

  getAllOrganizations(): Observable<any> {
    return this.apiService.get(`/v1/organizations`);
  }
}
